import { useLocation } from 'react-router-dom';
import { useMemo } from 'react';
import { removeSearchParams } from '../../core/util/removeSearchParam';
import updateSearchParams from '../../core/util/updateSearchParams';
import preSortedAttributes from '../util/preSortedAttributes';
import type { CategoryFilterWithValues } from '../types';
import useCustomNavigate from '../../core/hooks/router/useCustomNavigate';

export interface TyreFilterState {
    PIM_ATT_MATCHCODE: string;
    PIM_ATT_TIRE_WIDTH_MOTO: string[];
    PIM_ATT_TIRE_HIGHT_MOTO: string[];
    PIM_ATT_DIAMETER: string[];
    PIM_ATT_APPLICATION_RANGE: string[];
    PIM_ATT_ERP_MANUFACTURER: string[];
    PIM_ATT_LOAD_INDEX: string[];
    PIM_ATT_SPEEDINDEX: string[];
}

export type UpdateFilters = (newValues: Partial<TyreFilterState>) => void;

export const nonIntegratedTyreFilters = [
    'PIM_ATT_MATCHCODE',
    'PIM_ATT_APPLICATION_RANGE',
    'PIM_ATT_ERP_MANUFACTURER',
    'PIM_ATT_LOAD_INDEX',
    'PIM_ATT_SPEEDINDEX',
    /* istanbul ignore next -- @preserve */
];

const matchCodeResettableFilters = new Set([
    'PIM_ATT_TIRE_WIDTH_MOTO',
    'PIM_ATT_TIRE_HIGHT_MOTO',
    'PIM_ATT_DIAMETER',
]);

const getFilterValues = (selectedFilter: CategoryFilterWithValues) => {
    const { valueLabels, values } = selectedFilter;
    return selectedFilter.values
        .filter(a => a !== '')
        .map(val => ({
            label: valueLabels ? valueLabels[values.indexOf(val)] : val,
            value: `${val}`,
        }));
};

const getValuesFromSearchParams = (
    searchParams: URLSearchParams
): TyreFilterState => ({
    PIM_ATT_MATCHCODE: searchParams.get('PIM_ATT_MATCHCODE') || '',
    PIM_ATT_TIRE_WIDTH_MOTO: searchParams.getAll('PIM_ATT_TIRE_WIDTH_MOTO'),
    PIM_ATT_TIRE_HIGHT_MOTO: searchParams.getAll('PIM_ATT_TIRE_HIGHT_MOTO'),
    PIM_ATT_DIAMETER: searchParams.getAll('PIM_ATT_DIAMETER'),
    PIM_ATT_APPLICATION_RANGE: searchParams.getAll('PIM_ATT_APPLICATION_RANGE'),
    PIM_ATT_ERP_MANUFACTURER: searchParams.getAll('PIM_ATT_ERP_MANUFACTURER'),
    PIM_ATT_LOAD_INDEX: searchParams.getAll('PIM_ATT_LOAD_INDEX'),
    PIM_ATT_SPEEDINDEX: searchParams.getAll('PIM_ATT_SPEEDINDEX'),
});

export type GetOptionsForFilterName = (
    field: string
) => { value: string; label: string }[];

const useTyreFilter = (filters: CategoryFilterWithValues[]) => {
    const { pathname, search } = useLocation();
    const navigate = useCustomNavigate();
    const searchParams = new URLSearchParams(search);

    const selectedValues = getValuesFromSearchParams(searchParams);

    const currentValues = useMemo(() => {
        const updatedSearchParams = new URLSearchParams(search);
        return getValuesFromSearchParams(updatedSearchParams);
    }, [search]);

    const getOptionsForFilterName: GetOptionsForFilterName = (
        filter: string
    ) => {
        const selectedFilter = filters.find(f => f.field === filter);
        if (selectedFilter) {
            const result = getFilterValues(selectedFilter);

            if (!preSortedAttributes.includes(filter)) {
                result.sort((a, b) => a.label.localeCompare(b.label));
            }

            return result;
        }

        return [];
    };

    const updateFilters: UpdateFilters = newValues => {
        const newFilterValues = { ...currentValues, ...newValues };

        const shouldResetMatchCode = Object.keys(newValues).some(key =>
            matchCodeResettableFilters.has(key)
        );
        const shouldResetDimensions =
            Object.keys(newValues).length === 1 &&
            !!newValues.PIM_ATT_MATCHCODE;

        if (shouldResetMatchCode) {
            newFilterValues.PIM_ATT_MATCHCODE = '';
        }

        if (shouldResetDimensions) {
            newFilterValues.PIM_ATT_TIRE_WIDTH_MOTO = [];
            newFilterValues.PIM_ATT_TIRE_HIGHT_MOTO = [];
            newFilterValues.PIM_ATT_DIAMETER = [];
        }

        let updatedSearchParams = removeSearchParams(
            searchParams,
            Object.entries(newFilterValues).map(([field]) => field)
        );

        updatedSearchParams = updateSearchParams(
            updatedSearchParams,
            Object.entries(newFilterValues)
                .filter(([, value]) =>
                    Array.isArray(value) ? true : value !== ''
                )
                .map(([field, value]) => ({
                    name: field,
                    value,
                }))
        );

        navigate(`${pathname}?${updatedSearchParams.toString()}`, {
            preventScrollReset: true,
        });
    };

    const clearFilters = () =>
        navigate(pathname, {
            preventScrollReset: true,
        });

    const filtersApplied = useMemo(
        () => Object.values(currentValues).some(value => value.length > 0),
        [currentValues]
    );

    return {
        currentValues,
        selectedValues,
        getOptionsForFilterName,
        updateFilters,
        clearFilters,
        filtersApplied,
    };
};

export default useTyreFilter;
